import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { adminAuditApi } from "../lib/api";
import {
  Loader2, Search, RefreshCw, ChevronDown, ChevronRight,
  ScrollText, ChevronUp, X, UserRound,
} from "lucide-react";
import { cn } from "../lib/utils";

interface AuditRow {
  id: string;
  actor_type: string;
  actor_id: string | null;
  actor_identity: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  wedding_id: string | null;
  metadata: string | null;
  created_at: string;
}

function timeAgo(
  dateStr: string,
  language: string,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const diff = Date.now() - new Date(dateStr + "Z").getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("admin.auditTimeJustNow");
  if (mins < 60) return t("admin.auditTimeMinutes", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("admin.auditTimeHours", { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("admin.auditTimeDays", { count: days });
  return new Date(dateStr + "Z").toLocaleDateString(language);
}

const ACTOR_STYLE: Record<string, { dot: string; text: string; bg: string }> = {
  admin:     { dot: "bg-purple-500", text: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20" },
  organizer: { dot: "bg-blue-500",   text: "text-blue-400",   bg: "bg-blue-500/10 border-blue-500/20" },
  guest:     { dot: "bg-green-500",  text: "text-green-400",  bg: "bg-green-500/10 border-green-500/20" },
  system:    { dot: "bg-slate-400",  text: "text-slate-400",  bg: "bg-slate-500/10 border-slate-500/20" },
};

function actionStyle(action: string): string {
  const v = action.split(".").pop() ?? "";
  if (/login|logout|auth/.test(v))               return "text-blue-400 bg-blue-500/8 border-blue-500/20";
  if (/create|upload|add/.test(v))               return "text-green-400 bg-green-500/8 border-green-500/20";
  if (/update|patch|edit|change|config/.test(v)) return "text-amber-400 bg-amber-500/8 border-amber-500/20";
  if (/delete|archive|remove|ban/.test(v))       return "text-red-400 bg-red-500/8 border-red-500/20";
  return "text-muted-foreground bg-muted/40 border-border";
}

const ACTOR_OPTS = ["", "admin", "organizer", "guest", "system"] as const;

// ── Action Dropdown ────────────────────────────────────────────
function ActionDropdown({ actions, value, onChange, t }: {
  actions: string[];
  value: string;
  onChange: (v: string) => void;
  t: (k: string) => string;
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const filtered = query.trim()
    ? actions.filter((a) => a.toLowerCase().includes(query.toLowerCase()))
    : actions;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-2 h-[34px] px-3 rounded-lg border text-xs font-medium transition-colors cursor-pointer",
          value
            ? "border-primary/40 bg-primary/5 text-primary"
            : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
        )}
      >
        <span className="max-w-[160px] truncate">
          {value || t("admin.auditFilterAction")}
        </span>
        {value
          ? <X className="h-3 w-3 shrink-0" onClick={(e) => { e.stopPropagation(); onChange(""); }} />
          : <ChevronDown className="h-3 w-3 shrink-0" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
              <input
                autoFocus
                type="text"
                placeholder={t("admin.auditSearchAction")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-muted/40 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            <button
              onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors cursor-pointer",
                !value ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground"
              )}
            >
              {t("admin.auditActionsAll")}
            </button>

            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center">{t("admin.auditNoResults")}</p>
            ) : (
              filtered.map((a) => (
                <button
                  key={a}
                  onClick={() => { onChange(a); setOpen(false); setQuery(""); }}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors cursor-pointer",
                    value === a ? "bg-primary/10" : "hover:bg-primary/5"
                  )}
                >
                  <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded border flex-1 truncate", actionStyle(a))}>
                    {a}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────
export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [logs, setLogs]               = useState<AuditRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actions, setActions]         = useState<string[]>([]);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [hasMore, setHasMore]         = useState(false);

  // Chips: identity = exact match (@-içeren), search = fuzzy
  const [identityChip, setIdentityChip] = useState(() => searchParams.get("identity") ?? "");
  const [searchChip, setSearchChip]     = useState("");
  const [searchInput, setSearchInput]   = useState("");
  const [actorType, setActorType]       = useState("");
  const [action, setAction]             = useState("");

  function commitSearch() {
    const val = searchInput.trim();
    if (!val) return;
    if (val.includes("@")) setIdentityChip(val);
    else setSearchChip(val);
    setSearchInput("");
  }

  const buildQuery = useCallback((cursor?: string) => {
    const p = new URLSearchParams({ limit: "50" });
    if (identityChip) p.set("actor_identity", identityChip);
    if (searchChip)   p.set("search", searchChip);
    if (actorType)    p.set("actor_type", actorType);
    if (action)       p.set("action", action);
    if (cursor)       p.set("cursor", cursor);
    return `?${p}`;
  }, [identityChip, searchChip, actorType, action]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLogs([]);
    try {
      const res = await adminAuditApi.get<AuditRow[]>(buildQuery());
      setLogs(res.data ?? []);
      setHasMore((res.data ?? []).length >= 50);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [buildQuery]);

  const fetchMoreRef = useRef(false);
  const fetchMore = useCallback(async () => {
    if (fetchMoreRef.current || !logs.length) return;
    fetchMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await adminAuditApi.get<AuditRow[]>(buildQuery(logs[logs.length - 1].created_at));
      const rows = res.data ?? [];
      setLogs((p) => [...p, ...rows]);
      setHasMore(rows.length >= 50);
    } catch { /* ignore */ }
    finally { fetchMoreRef.current = false; setLoadingMore(false); }
  }, [buildQuery, logs]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => {
    adminAuditApi.get<string[]>("/actions").then((r) => setActions(r.data ?? [])).catch(() => {});
  }, []);

  const actorLabels: Record<string, string> = {
    "": t("admin.auditActorAll"),
    admin:     t("admin.auditActorAdmin"),
    organizer: t("admin.auditActorOrganizer"),
    guest:     t("admin.auditActorGuest"),
    system:    t("admin.auditActorSystem"),
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("admin.auditLog")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.auditDescription")}</p>
        </div>
        <button onClick={fetchLogs}
          className="shrink-0 p-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">

        {/* Active filter chips */}
        {identityChip && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-primary/40 bg-primary/5 text-xs font-medium text-primary">
            <UserRound className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[180px]">{identityChip}</span>
            <button onClick={() => setIdentityChip("")} className="shrink-0 hover:text-foreground cursor-pointer ml-0.5">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        {searchChip && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-muted/50 text-xs font-medium text-muted-foreground">
            <Search className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[180px]">{searchChip}</span>
            <button onClick={() => setSearchChip("")} className="shrink-0 hover:text-foreground cursor-pointer ml-0.5">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Unified search — @içeriyorsa kimlik chip'i, yoksa arama chip'i */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder={t("admin.auditSearchPlaceholder")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") commitSearch();
              if (e.key === "Escape") setSearchInput("");
            }}
            className="w-full pl-9 pr-3 py-1.5 text-sm bg-muted/40 border border-primary/20 rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-[border-color,box-shadow] placeholder:text-muted-foreground/50"
          />
          {searchInput && (
            <button onClick={() => setSearchInput("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Actor type pills */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {ACTOR_OPTS.map((opt) => (
            <button key={opt}
              onClick={() => setActorType(opt)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                actorType === opt
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-primary/5 hover:text-foreground"
              )}>
              {actorLabels[opt]}
            </button>
          ))}
        </div>

        {/* Action dropdown */}
        <ActionDropdown actions={actions} value={action} onChange={setAction} t={t} />

        {!loading && (
          <span className="text-xs text-muted-foreground ml-auto">
            {t("admin.auditRecordCount", { count: logs.length })}{hasMore ? "+" : ""}
          </span>
        )}
      </div>

      {/* Log table */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-center rounded-2xl border border-border">
          <ScrollText className="h-8 w-8 text-primary/30" />
          <p className="text-sm text-muted-foreground">{t("admin.auditNoResults")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    {[
                      t("admin.auditColTime"),
                      t("admin.auditColActor"),
                      t("admin.auditColIdentity"),
                      t("admin.auditColAction"),
                      t("admin.auditColResource"),
                      "",
                    ].map((h, i) => (
                      <th key={i} className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {logs.map((row) => {
                    const actor = ACTOR_STYLE[row.actor_type] ?? ACTOR_STYLE.system;
                    const isExpanded = expandedId === row.id;
                    const identity = row.actor_identity
                      ?? (row.actor_type === "guest" ? `${t("admin.auditGuestPrefix")}·${row.actor_id?.slice(0, 6) ?? "?"}` : row.actor_id?.slice(0, 8))
                      ?? "—";

                    return [
                      <tr key={row.id}
                        onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        className="transition-colors cursor-pointer hover:bg-primary/5">

                        {/* Time */}
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className="text-sm text-muted-foreground" title={new Date(row.created_at + "Z").toLocaleString(i18n.language)}>
                            {timeAgo(row.created_at, i18n.language, t)}
                          </span>
                        </td>

                        {/* Actor badge */}
                        <td className="px-5 py-3.5">
                          <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold", actor.bg)}>
                            <span className={cn("h-1.5 w-1.5 rounded-full", actor.dot)} />
                            <span className={actor.text}>{actorLabels[row.actor_type] ?? row.actor_type}</span>
                          </span>
                        </td>

                        {/* Identity */}
                        <td className="px-5 py-3.5 max-w-[160px]">
                          <span className="text-xs text-muted-foreground truncate block">{identity}</span>
                        </td>

                        {/* Action chip */}
                        <td className="px-5 py-3.5">
                          <span className={cn("font-mono text-[11px] px-2 py-1 rounded-lg border", actionStyle(row.action))}>
                            {row.action}
                          </span>
                        </td>

                        {/* Resource — truncated, full value in tooltip + expanded row */}
                        <td className="px-5 py-3.5 max-w-[140px]"
                          title={row.resource_id ? `${row.resource_type}: ${row.resource_id}` : row.resource_type}>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-xs text-muted-foreground truncate">{row.resource_type}</span>
                            {row.resource_id && (
                              <span className="shrink-0 font-mono text-[10px] text-muted-foreground/50">{row.resource_id.slice(0, 6)}…</span>
                            )}
                          </div>
                        </td>

                        {/* Expand chevron */}
                        <td className="px-5 py-3.5 w-8">
                          {isExpanded
                            ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        </td>
                      </tr>,

                      isExpanded && (
                        <tr key={`${row.id}-meta`}>
                          <td colSpan={6} className="px-5 py-4 bg-muted/20 space-y-3">
                            {/* Resource full value */}
                            {row.resource_id && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{t("admin.auditColResource")}</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-muted-foreground">{row.resource_type}</span>
                                  <code className="font-mono text-xs bg-background/60 border border-border rounded px-2 py-0.5 select-all break-all">
                                    {row.resource_id}
                                  </code>
                                </div>
                              </div>
                            )}
                            {/* Metadata */}
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{t("admin.auditMetadata")}</p>
                              {row.metadata ? (
                                <pre className="text-xs font-mono text-foreground/80 whitespace-pre-wrap break-all max-h-56 overflow-auto bg-background/60 rounded-xl p-3 border border-border">
                                  {(() => { try { return JSON.stringify(JSON.parse(row.metadata), null, 2); } catch { return row.metadata; } })()}
                                </pre>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">{t("admin.auditMetadataNone")}</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button onClick={fetchMore} disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("common.loadMore")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
